import { AuthorizedService } from "matchi-api";
import { addBreadcrumb } from "@sentry/react";
import { track } from "@/analytics";
import type { PaymentConfig } from "@/services";
import { PaymentService } from "@/services";
import { emitPaymentServiceEvent } from "@/hooks/usePaymentServiceEvents";
import type { PaymentServiceCore } from "@/types/adyen";

/**
 * Names the provider we're handing off to, without the per-transaction detail.
 *
 * `hostname` rather than `host`, so a port doesn't split one provider into two
 * values. And for app-scheme redirects — Swish hands off as
 * `swish://paymentrequest?token=…` — the authority is a path fragment, not a
 * host, so the scheme is the answer to "which provider" there.
 */
const providerName = (url?: string) => {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return parsed.hostname;
    }
    return parsed.protocol.replace(/:$/, "");
  } catch {
    return undefined;
  }
};

/**
 * We need to remove the Adyen redirect params from the URL before creating the pspSession
 * to make sure whe get the same session id when we are redirected back to the checkout.
 */
const ADYEN_REDIRECT_PARAMS = ["redirectResult", "sessionId"];
const getReturnUrl = () => {
  if (typeof window === undefined) return "";

  const url = new URL(window.location.href);
  ADYEN_REDIRECT_PARAMS.forEach(key => url.searchParams.delete(key));

  return url.toString();
};

export const getPaymentServiceQueryKey = (token: string) => [
  "paymentService",
  token,
];

export const getPaymentServiceQuery = (
  token: string,
  paymentConfig?: PaymentConfig,
) => ({
  queryKey: getPaymentServiceQueryKey(token),
  queryFn: async () => {
    const pspSession = await AuthorizedService.getPspSession(
      token,
      getReturnUrl(),
    );

    const core = await PaymentService.init({
      session: {
        id: pspSession.pspSessionId,
        sessionData: pspSession.pspSessionData,
      },
      showPayButton: false,
      analytics: { enabled: false },
      onPaymentCompleted: (data, element) => {
        emitPaymentServiceEvent("onPaymentCompleted", { data, element });
      },
      onError: (error, element) => {
        emitPaymentServiceEvent("onError", { error, element });
      },
      beforeRedirect: async (resolve, reject, redirectData) => {
        addBreadcrumb({
          category: "payment",
          message: "Redirecting to payment provider",
          data: { url: redirectData?.url, method: redirectData?.method },
        });
        track("Payment Redirect Started", {
          checkout_token: token,
          // Host only — see the note on the event in analytics/events.ts. The
          // full URL stays on the Sentry breadcrumb above.
          provider_host: providerName(redirectData?.url),
          method: redirectData?.method,
        });
        resolve();
      },
      onActionHandled: (actionHandled) => {
        addBreadcrumb({
          category: "payment",
          message: actionHandled.actionDescription,
          data: { componentType: actionHandled.componentType },
        });
        track("Payment Action Handled", {
          checkout_token: token,
          action: actionHandled.actionDescription,
          component_type: actionHandled.componentType,
        });
      },
      ...paymentConfig,
      // v5's CoreOptions type doesn't include beforeRedirect/onActionHandled/analytics
      // but the SDK accepts them at runtime — assert to satisfy TypeScript.
    } as PaymentConfig);

    // Attach enableRecurring to the core so consumers can read it without
    // changing the PaymentService.init() signature or fetching pspSession twice.
    const coreWithRecurring = core as PaymentServiceCore;
    coreWithRecurring.enableRecurring = pspSession.enableRecurring;

    return coreWithRecurring;
  },
});
